import type { RcFile } from 'antd/es/upload/interface'
import type { EditControlProps } from '../../declare'
import { useState, useEffect, useContext } from 'react'
import { toJS } from 'mobx'
import { Upload, Button } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { EditContext, postMsg } from 'src/components'
import { appStore } from 'src/stores'
import { getImgBase, getApiURL } from 'src/utils/axios'
import { guid } from 'src/utils/tools'
import { cloneDeep, omit, set } from 'lodash'

interface FileItem extends RcFile {
  IS_DELETED: boolean,
  Data: any
}

const AttachUpload = (props: EditControlProps) => {
  const edit = useContext(EditContext)

  const [initValue, setInitValue] = useState<any>()
  useEffect(() => {
    if (props.value && !initValue) {
      const targetValue = cloneDeep(toJS(props.value))
      setInitValue(targetValue)
      props.onChange?.(targetValue.map((val: any) => omit(val, ['Nav_ImgFile'])))
    }
  }, [props.value, initValue])

  const [fileList, setFileList] = useState<FileItem[]>([])
  useEffect(() => {
    setFileList(initValue?.filter((item: any) => !item.IS_DELETED).map((item: any) => {
      return {
        uid: item.ID,
        name: item.Nav_ImgFile?.FILE_NAME ?? '新建文件',
        status: 'done',
        url: `${getImgBase()}${item.Nav_ImgFile?.Nav_File?.FILE_PATH}`,
      }
    }) ?? [])
  }, [initValue])

  const handleUpload = (file: RcFile) => {
    const { Tenant = null, OrgId } = appStore.current.headers
    const { INPUT_ID_FIELD, FILE_SIZE: fileSize } = props.config

    if (fileSize && file.size / 1024 / 1024 > fileSize) {
      postMsg(`上传文件超过${fileSize}MB，请重新选择`, 'error')
      return
    }

    const formData = new FormData()
    formData.append('OrgId', OrgId)
    formData.append('file', file)

    fetch(getApiURL('/PF/File/UploadFileToImage'), {
      method: 'POST',
      headers: { Tenant, OrgId },
      body: formData
    }).then(res => res.json())
      .then(res => {
        if (res && res.IsSuccessful) {
          postMsg('上传成功')
          const { imgFileID, imgFilePath } = res.Data
          const uploadedFiles = {
            ID: guid(),
            IMG_FILE_ID: imgFileID,
            ORG_ID: OrgId,
            Nav_ImgFile: {
              FILE_NAME: file.name,
              Nav_File: { FILE_PATH: imgFilePath }
            }
          }
          INPUT_ID_FIELD && set(uploadedFiles, INPUT_ID_FIELD, edit.formBase.ID)
          setInitValue((prev: any) => [uploadedFiles].concat(prev).filter(item => !!item))
          props.onChange?.([omit(uploadedFiles, ['Nav_ImgFile'])].concat(props.value).filter(item => !!item))
        } else {
          postMsg('上传失败', 'error')
        }
      })
    return false
  }

  const handleRemove = (file: any) => {
    const copyData = cloneDeep(initValue)
    const find = copyData.find((item: any) => item.Nav_ImgFile?.FILE_NAME === file.name)
    if (find) {
      find.IS_DELETED = true
      setInitValue(copyData)
      props.onChange?.(copyData.map((val: any) => omit(val, ['Nav_ImgFile'])))
    }
  }

  const { __READONLY__, EDITABLE, FILE_TYPES } = props.config
  const disabled = __READONLY__ || !EDITABLE || props.evtConfig?.disabled
  return (
    <Upload
      disabled={disabled}
      accept={FILE_TYPES}
      fileList={fileList}
      beforeUpload={handleUpload}
      onRemove={handleRemove}
    >
      <Button icon={<UploadOutlined />} disabled={disabled}>选择文件</Button>
    </Upload>
  )
}

export default AttachUpload